import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import Icon from '~/app/common/Icon';
import useTranslation from '~/app/hooks/useTranslation';
import { notify } from '~/app/helpers/notify';
import { useStoreActions, useStoreState } from '~/store';
import { WorkOrderDetail } from '~/workorder/types';
import classes from './style.module.scss';
import {
    getFooterActionIcon,
    getButtonColor,
    WorkOrderFooterAction,
    getVisibleWorkOrderFooterActions,
    getTargetStatusForFooterAction,
    buildSaveWorkOrderPayload,
    areAllWorkOrderTasksDone,
    validateWorkOrderAdministrativeFields,
    isWorkOrderAdministrativeValidationRequired,
    validateWorkOrderOperatingTime,
    validateWorkOrderOperatingCount,
} from './helper';

export type FooterComponent = React.FC<{ data: WorkOrderDetail, isFooterOpen: boolean, setIsFooterOpen: (isFooterOpen: boolean) => void }>;

const getActionButtonClass = (action: WorkOrderFooterAction): string | undefined => {
    const color = getButtonColor(action);
    switch (color) {
        case 'red':
            return classes.actionButtonRed;
        case 'green':
            return classes.actionButtonGreen;
        case 'primary':
            return classes.actionButtonPrimary;
        case 'white':
            return classes.actionButtonDefault;
        default:
            return classes.actionButtonDefault;
    }
};

const Footer: FooterComponent = ({ data, isFooterOpen, setIsFooterOpen }) => {
    const { t } = useTranslation('workorder');
    const fetching = useStoreState(state => state.workOrder.fetching);
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const workOrderData = useStoreState(state => state.workOrder.data);
    const taskCount = useStoreState(state => state.workOrder.taskCount);
    const costCategories = useStoreState(state => state.issue.costCategories);
    const customers = useStoreState(state => state.issue.customers);
    const { updateWorkOrderStatus, getWorkOrderDetail, saveWorkOrder } = useStoreActions(state => state.workOrder);

    const actions = useMemo(
        () => getVisibleWorkOrderFooterActions(data.status),
        [data.status],
    );

    const stackActions = useMemo(() => actions.slice().reverse(), [actions]);

    const refreshDetailAndCloseFooter = useCallback(
        (workOrderId: number) => getWorkOrderDetail({ id: workOrderId, showLoading: false }).then(() => setIsFooterOpen(false)),
        [getWorkOrderDetail, setIsFooterOpen],
    );

    const onAction = useCallback((action: WorkOrderFooterAction) => {
        const workOrderId = data.id;

        if (isWorkOrderAdministrativeValidationRequired(action)) {
            const adminError = validateWorkOrderAdministrativeFields(
                workOrderData,
                costCategories,
                customers,
            );
            if (adminError) {
                notify(t(`error.${adminError}`), 'error');
                return;
            }
        }

        if (action === WorkOrderFooterAction.Save) {
            saveWorkOrder(buildSaveWorkOrderPayload(workOrderData))
                .then((response) => {
                    if (response.success) {
                        return refreshDetailAndCloseFooter(workOrderId);
                    }
                });
            return;
        }

        if (action === WorkOrderFooterAction.Complete) {
            if (!areAllWorkOrderTasksDone(taskCount)) {
                notify(t('error.completeTasksRequired'), 'error');
                return;
            }

            if (!validateWorkOrderOperatingTime(workOrderData)) {
                notify(t('error.operatingTimeRequired'), 'error');
                return;
            }

            if (!validateWorkOrderOperatingCount(workOrderData)) {
                notify(t('error.operatingCountRequired'), 'error');
                return;
            }
        }

        const targetStatus = getTargetStatusForFooterAction(action);
        if (targetStatus === null) {
            return;
        }

        updateWorkOrderStatus({ workOrderId, status: targetStatus })
            .then((response) => {
                if (response.success) {
                    return refreshDetailAndCloseFooter(workOrderId);
                }
            });
    }, [data.id, workOrderData, taskCount, costCategories, customers, saveWorkOrder, updateWorkOrderStatus, refreshDetailAndCloseFooter, t]);

    if (netWorkStatus !== 0 || actions.length === 0 || fetching) {
        return null;
    }

    return (
        <div className={classes.fabRoot}>
            {isFooterOpen && (
                <div className={classes.stack} role="menu">
                    {stackActions.map(action => {
                        const label = t(`footer.${action}`);
                        return (
                            <div key={action} className={classes.actionRow} role="none">
                                <span className={classes.label}>{label}</span>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className={classNames(
                                        classes.actionButton,
                                        getActionButtonClass(action),
                                    )}
                                    aria-label={label}
                                    onClick={() => onAction(action)}
                                >
                                    <Icon name={getFooterActionIcon(action)} size={22} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
            <button
                type="button"
                className={classes.toggleFab}
                aria-expanded={isFooterOpen}
                onClick={() => setIsFooterOpen(!isFooterOpen)}
            >
                <Icon name={isFooterOpen ? 'close' : 'touch_app'} size={28} />
            </button>
        </div>
    );
};

export default Footer;
